import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Layers, Target, Zap, Wrench, Users, TrendingUp } from 'lucide-react';

const iconComponents = [Layers, Target, Zap, Wrench, Users, TrendingUp];

const WhyMe = () => {
  const { t, i18n } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const isRTL = i18n.language === 'ar';
  const items = t('whyMe.items', { returnObjects: true });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="why-me"
      ref={sectionRef}
      className="relative py-24 md:py-32 bg-[#080808]"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 grid-pattern opacity-20" />
      
      {/* Accent Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#00FFD1]/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-[7.6923%]">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div
            className={`inline-flex items-center gap-3 mb-6 opacity-0 ${
              isVisible ? 'animate-fade-in-up' : ''
            }`}
          >
            <span className="w-12 h-[2px] bg-[#00FFD1]" />
            <span className="text-[#00FFD1] text-sm font-medium tracking-wider uppercase">
              {t('whyMe.title')}
            </span>
            <span className="w-12 h-[2px] bg-[#00FFD1]" />
          </div>

          <h2
            className={`display-large text-white mb-4 opacity-0 ${
              isVisible ? 'animate-fade-in-up delay-100' : ''
            }`}
          >
            {t('whyMe.subtitle')}
          </h2>
        </div>

        {/* Differentiators Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item, index) => {
            const IconComponent = iconComponents[index];
            return (
              <div
                key={item.title}
                className={`group relative p-8 bg-white/[0.02] border border-white/10 hover:border-[#00FFD1]/30 transition-all duration-500 hover-glow opacity-0 ${
                  isVisible ? `animate-fade-in-up delay-${(index + 2) * 100}` : ''
                }`}
              >
                {/* Icon */}
                <div className="w-12 h-12 flex items-center justify-center bg-[#00FFD1]/10 mb-6 group-hover:bg-[#00FFD1]/20 transition-colors duration-500">
                  <IconComponent className="w-6 h-6 text-[#00FFD1]" />
                </div>

                {/* Title */}
                <h3 className="text-lg font-semibold text-white mb-3 group-hover:text-[#00FFD1] transition-colors duration-300">
                  {item.title}
                </h3>

                {/* Description */}
                <p className="text-white/60 text-sm leading-relaxed">
                  {item.description}
                </p>

                {/* Corner Accents */}
                <div className={`absolute top-0 ${isRTL ? 'left-0 border-t border-l' : 'right-0 border-t border-r'} w-6 h-6 border-[#00FFD1]/0 group-hover:border-[#00FFD1]/50 transition-all duration-500`} />
                <div className={`absolute bottom-0 ${isRTL ? 'right-0 border-b border-r' : 'left-0 border-b border-l'} w-6 h-6 border-[#00FFD1]/0 group-hover:border-[#00FFD1]/50 transition-all duration-500`} />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhyMe;
